import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import BackToCockpit from "@/components/BackToCockpit";
import IdPhotoEnhanceModal from "@/components/IdPhotoEnhanceModal";
import FlashBadgeButton from "@/components/FlashBadgeButton";
import { DIGITAL_ID_PRICE, DIGITAL_ID_LOOKUP_KEY } from "@/data/pjp";
import { ShieldCheck, QrCode, Plane, ArrowRight, ExternalLink, Camera, Loader2, Sparkles, Lock } from "lucide-react";
import { isPro } from "@/lib/permissions";

/**
 * /digital-id
 *   • Logged-out visitors → marketing pitch (existing hero + demo card + perks).
 *   • Logged-in members   → their FULL live badge inline (via the /id/:token
 *     route rendered inside an iframe) + an AI-powered "Update ID Photo" flow.
 */
export default function DigitalID() {
  const { user, refresh } = useAuth();
  const [token, setToken] = useState(null);
  const [showPhoto, setShowPhoto] = useState(false);

  // Members who haven't purchased Digital ID (or bundled it with Pro) see a
  // locked variant with an upgrade CTA — NOT the auto-provision call, which
  // would 402 and pop the global modal.
  const idPurchased = !!user && (user.pjp_id_status === "active" || user.founding_member || user.is_admin);

  // Auto-provision the user's public token (idempotent) — only for entitled users.
  useEffect(() => {
    if (!user || !idPurchased) return;
    let ok = true;
    api.post("/id/mine/enable", null, { silent402: true })
      .then((r) => { if (ok) setToken(r.data.public_token); })
      .catch(() => {});
    return () => { ok = false; };
  }, [user?.id, idPurchased]);

  if (user && !idPurchased) return <LockedView user={user} />;
  if (user) return <LoggedInView user={user} token={token} showPhoto={showPhoto} setShowPhoto={setShowPhoto} onSaved={refresh} />;
  return <AnonView />;
}

/* ============================== LOCKED ============================== */
// Signed in but hasn't purchased Digital ID yet. Marketing card + purchase CTA.

function LockedView({ user }) {
  const buy = async () => {
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: DIGITAL_ID_LOOKUP_KEY,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };
  const showBundle = !isPro(user);
  return (
    <div className="max-w-3xl mx-auto px-6 py-14">
      <BackToCockpit />
      <div className="overline text-orange-hud mb-2">// DIGITAL ID · LOCKED</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-tight mb-3">
        Get your PJP Digital ID Badge.
      </h1>
      <p className="text-white/70 max-w-xl mb-8">
        Your physical NFC badge is an annual <b className="text-white">${DIGITAL_ID_PRICE}/year</b> subscription. FAA cross-checked, wallet-ready,
        recognized by 1,240+ operators and FBOs. Ships in 24-48h.
      </p>

      <Card className="!border-orange-hud/40" testId="digital-id-locked-card">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg border border-orange-hud/40 bg-orange-hud/10 flex-shrink-0">
            <Lock className="h-6 w-6 text-orange-hud" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold mb-1">Your badge isn't active yet.</div>
            <div className="text-sm text-white/65 mb-4">
              You'll get a full HUD-styled badge (like the demo below), an NFC-scannable physical card mailed to you,
              and a shareable public verify URL. Once activated, this page becomes your live badge.
            </div>
            <div className="flex flex-wrap gap-2 mb-1">
              <button
                onClick={buy}
                data-testid="digital-id-buy-btn"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-orange-hud text-obsidian font-display font-semibold text-sm shadow-[0_0_20px_-4px_rgba(255,138,61,0.6)] hover:brightness-105 transition-all"
              >
                Buy Digital ID — ${DIGITAL_ID_PRICE}/year <ArrowRight className="h-3.5 w-3.5" />
              </button>
              {showBundle && (
                <Link
                  to="/membership/addon?tier=pro"
                  data-testid="digital-id-bundle-btn"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-cyan/40 text-cyan text-sm hover:border-cyan hover:bg-cyan/[0.10] transition-colors"
                >
                  Bundle with Pro — $328 total <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
              <Link to="/digital-id/demo" data-testid="digital-id-preview-btn" className="inline-flex items-center gap-1.5 mono text-[11px] tracking-widest uppercase text-white/55 hover:text-white px-3 py-2">
                See the badge preview <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ============================== LOGGED-IN ============================== */

function LoggedInView({ user, token, showPhoto, setShowPhoto, onSaved }) {
  const [flashStats, setFlashStats] = useState(null);
  useEffect(() => {
    api.get("/id/flash/history", { params: { limit: 5 } })
      .then((r) => setFlashStats(r.data))
      .catch(() => {});
  }, [user?.id]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <BackToCockpit />
      <div className="overline text-cyan mb-2">PJP DIGITAL ID · YOUR BADGE</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
          {user.nickname ? user.nickname : `${user.first_name} ${user.last_name}`}
        </h1>
        <button
          data-testid="did-update-photo"
          onClick={() => setShowPhoto(true)}
          className="btn-hud btn-hud-solid"
        >
          <Camera className="h-3.5 w-3.5" /> UPDATE ID PHOTO
          <span className="mono text-[9px] tracking-widest ml-1 opacity-70">AI</span>
        </button>
      </div>
      <p className="mt-3 text-sm text-white/60 max-w-2xl">
        Your live PJP badge — FAA cross-verified, wallet-ready, and instantly shareable. Tap NFC or scan the QR to verify.
      </p>

      {/* Flash Badge — big "show my ID now" CTA for FBOs / ramp / hangar. */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <FlashBadgeButton variant="inline" label="FLASH BADGE" testId="did-flash-badge" />
        <div className="text-[11px] text-white/50 max-w-sm">
          One-tap fullscreen ID for FBOs, ramp crews, or ground handlers. Screen stays awake · auto-closes in 90s.
        </div>
      </div>

      {/* Flash activity — social-proof strip that also feeds the ranking algo. */}
      {flashStats && flashStats.total > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="did-flash-activity">
          <span className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/70">
            // {flashStats.total} lifetime flash{flashStats.total === 1 ? "" : "es"}
          </span>
          {(flashStats.by_airport_30d || []).length > 0 && (
            <span className="mono text-[10px] tracking-widest text-white/40">·  Last 30 days:</span>
          )}
          {(flashStats.by_airport_30d || []).slice(0, 5).map((a) => (
            <span
              key={a.icao}
              data-testid={`did-flash-airport-${a.icao}`}
              className="mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border border-cyan/30 bg-cyan/[0.06] text-cyan/90 flex items-center gap-1.5"
              title={a.name}
            >
              {a.icao}
              <span className="text-white/60 text-[9px]">×{a.count}</span>
            </span>
          ))}
        </div>
      )}

      {/* Full live badge — iframe of /id/:token so we always render whatever the
          badge page renders in production. embed=1 hides site chrome. */}
      <div className="mt-6 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <Card className="!p-4 relative overflow-hidden" style={{ background: "rgba(5,8,16,0.55)" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// LIVE BADGE</div>
            <a
              href={token ? `/id/${token}` : "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="did-open-live"
              className={`mono text-[10px] tracking-widest uppercase flex items-center gap-1 ${token ? "text-cyan hover:text-white" : "text-white/30 pointer-events-none"}`}
            >
              OPEN FULL BADGE <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="w-full flex justify-center">
            {token ? (
              <div
                className="rounded-xl overflow-hidden border border-white/10 bg-black/40"
                style={{ width: 420, maxWidth: "100%", aspectRatio: "1 / 2" }}
              >
                <iframe
                  data-testid="did-live-iframe"
                  src={`/id/${token}?embed=1`}
                  title="PJP Digital ID"
                  className="block"
                  style={{ width: 420, height: 840, border: 0, background: "transparent" }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 w-full text-white/50">
                <Loader2 className="h-5 w-5 animate-spin text-cyan mr-2" /> Loading your badge…
              </div>
            )}
          </div>
        </Card>

        {/* Side panel — quick facts + share */}
        <div className="space-y-4">
          <Card className="!p-5">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-2">// Card details</div>
            <FactRow k="PJP-ID" v={user.pjp_member_number ? String(user.pjp_member_number).padStart(5, "0") : "—"} />
            <FactRow k="Role" v={(user.role || "").toUpperCase()} />
            <FactRow k="Cert #" v={user.pilot_certificate_number || "—"} />
            <FactRow k="Hours" v={(user.total_hours || 0).toLocaleString()} />
            <FactRow k="Status" v={user.faa_verified ? "VERIFIED" : "PENDING"} tone={user.faa_verified ? "avail" : "amber"} />
            <FactRow k="Photo" v={user.id_photo_url ? "On file" : "Not set"} tone={user.id_photo_url ? "avail" : "amber"} last />
          </Card>

          <Card className="!p-5">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-2">// Share your badge</div>
            <p className="text-[12px] text-white/55 mb-3">Add to Apple Wallet / Google Wallet, or share the direct link. Anyone with the URL sees a read-only badge.</p>
            <div className="flex flex-wrap gap-2">
              <a
                href={token ? `/id/${token}` : "#"}
                target="_blank"
                rel="noreferrer"
                data-testid="did-share-link"
                className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-cyan/40 text-cyan hover:bg-cyan/10 flex items-center gap-1.5"
              >
                LIVE URL <ExternalLink className="h-3 w-3" />
              </a>
              <button
                type="button"
                onClick={() => {
                  if (!token) return;
                  navigator.clipboard.writeText(`${window.location.origin}/id/${token}`);
                }}
                data-testid="did-copy-link"
                className="mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-white/15 text-white/70 hover:bg-white/5"
              >
                Copy link
              </button>
            </div>
          </Card>

          <div
            className="rounded-lg p-4 border border-cyan/25 relative overflow-hidden"
            style={{ background: "linear-gradient(150deg, rgba(75,156,255,0.10), rgba(5,8,16,0.85))" }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-3.5 w-3.5 text-cyan" />
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan">AI photo assistant</div>
            </div>
            <div className="text-[12px] text-white/70 leading-relaxed">
              Upload any headshot — our AI checks it against ID standards, removes the background, and gives you a clean studio-grade version.
            </div>
            <button
              onClick={() => setShowPhoto(true)}
              data-testid="did-update-photo-side"
              className="mt-3 mono text-[10px] tracking-widest uppercase text-cyan hover:text-white inline-flex items-center gap-1"
            >
              LAUNCH PHOTO STUDIO <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      <IdPhotoEnhanceModal open={showPhoto} onClose={() => setShowPhoto(false)} onSaved={onSaved} />
    </div>
  );
}

function FactRow({ k, v, tone, last }) {
  const toneCls = tone === "avail" ? "text-avail" : tone === "amber" ? "text-amber-300" : "text-white";
  return (
    <div className={`flex items-baseline justify-between py-1.5 ${last ? "" : "border-b border-white/[0.04]"}`}>
      <div className="mono text-[10px] tracking-widest uppercase text-white/40">{k}</div>
      <div className={`mono text-[12px] ${toneCls}`}>{v}</div>
    </div>
  );
}

/* ================================ ANON ================================ */

function AnonView() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="overline text-cyan mb-3">PJP DIGITAL ID</div>
      <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-none">Your professional aviation identity.</h1>
      <p className="mt-5 text-slate-300 max-w-3xl">
        A shareable wallet-ready badge. FAA-verified. Linkable from your profile. Accepted as
        professional verification across the private aviation industry.
      </p>

      <div className="mt-12 grid md:grid-cols-2 gap-8 items-center">
        <div className="relative">
          <div className="absolute inset-0 bg-cyan/10 blur-3xl opacity-40" />
          <div className="relative card-hud hud-corners p-6" style={{ background: "linear-gradient(160deg, #121C26 0%, #0A0F16 100%)" }}>
            <span className="hud-c-bl" /><span className="hud-c-br" />
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline text-cyan mb-1">PJP · DIGITAL ID</div>
                <div className="font-mono text-[10px] text-slate-500">v.2026.1 · FAA CROSS-VERIFIED</div>
              </div>
              <div className="h-16 w-16 border border-cyan/40 flex items-center justify-center bg-cyan/5">
                <QrCode className="h-8 w-8 text-cyan" />
              </div>
            </div>
            <div className="mb-6">
              <div className="font-display text-3xl font-bold tracking-tight">Capt. Alex Rivera</div>
              <div className="mt-1 font-mono text-cyan text-sm">AIRLINE TRANSPORT PILOT · CE-680 · G550</div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div><div className="overline">CERT #</div><div className="font-mono text-sm text-slate-100">A4238915</div></div>
              <div><div className="overline">HOURS</div><div className="font-mono text-sm text-slate-100">12,400</div></div>
              <div><div className="overline">MEMBER</div><div className="font-mono text-sm text-slate-100">PJP · SINCE 2020</div></div>
              <div><div className="overline">STATUS</div><div className="font-mono text-sm text-avail">VERIFIED</div></div>
            </div>
            <div className="border-t border-cyan/10 pt-3 flex items-center justify-between">
              <div className="overline">PRIVATEJETPILOTS.COM</div>
              <Badge tone="green" pulse>ACTIVE</Badge>
            </div>
          </div>
        </div>

        <div>
          <Badge tone="orange" pulse>+ ${DIGITAL_ID_PRICE} / YEAR ADD-ON</Badge>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-4">Verified. Wallet-ready. Recognized.</h2>
          <ul className="mt-6 space-y-4">
            {[
              { icon: ShieldCheck, t: "FAA cross-verified", d: "Cross-checked against the FAA Airmen Registry every 90 days." },
              { icon: QrCode, t: "Scan-anywhere QR", d: "Add to Apple Wallet / Google Wallet. Employers scan and verify instantly." },
              { icon: Plane, t: "Ratings & type list", d: "Displays your active certificates, type ratings, and current medical on one card." },
            ].map((x) => (
              <li key={x.t} className="flex items-start gap-3">
                <x.icon className="h-5 w-5 text-cyan mt-0.5" />
                <div>
                  <div className="font-display text-lg font-semibold">{x.t}</div>
                  <div className="text-sm text-slate-400">{x.d}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <Link to="/membership" data-testid="did-cta-join" className="btn-hud btn-hud-solid">ADD DIGITAL ID <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/login" data-testid="did-cta-signin" className="btn-hud">SIGN IN</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
